import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Globe } from "lucide-react";

import { localizePath, normalizeLanguage, stripLocalePrefix } from "@/navigations";
import { LanguageEnum } from "@/types";

import styles from "./styles.module.css";

const LANGUAGES: { value: LanguageEnum; key: string }[] = [
  { value: LanguageEnum.EN, key: "languageSwitcher.english" },
  { value: LanguageEnum.ZH, key: "languageSwitcher.chinese" },
  { value: LanguageEnum.FR, key: "languageSwitcher.french" },
];

export const LanguageSwitcher = memo(() => {
  const { i18n, t } = useTranslation("components");
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = normalizeLanguage(i18n.language);
  const currentLang = LANGUAGES.find((l) => l.value === current);
  const currentLabel = currentLang ? t(currentLang.key) : current.toUpperCase();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleChange = useCallback(
    (lng: LanguageEnum) => {
      void i18n.changeLanguage(lng);
      const basePath = stripLocalePrefix(location.pathname);
      navigate(`${localizePath(basePath, lng)}${location.search}${location.hash}`, { replace: true });
      setOpen(false);
    },
    [i18n, location.pathname, location.search, location.hash, navigate],
  );

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-label={t("languageSwitcher.ariaLabel")}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Globe size={15} className={styles.globeIcon} />
        <span className={styles.triggerLabel}>{currentLabel}</span>
        <ChevronDown size={14} className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} />
      </button>
      {open && (
        <ul className={styles.dropdown} role="listbox">
          {LANGUAGES.map(({ value, key }) => (
            <li key={value} role="option" aria-selected={current === value}>
              <button
                type="button"
                className={current === value ? styles.itemActive : styles.item}
                onClick={() => handleChange(value)}
              >
                {t(key)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

LanguageSwitcher.displayName = "LanguageSwitcher";
export default LanguageSwitcher;
